// UploadScoreEngineResult.tsx
import React, { useEffect, useState } from "react";
import axios from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API_BASE_URL } from "@/config";
import { useLoader } from "@/contexts/LoaderContext";
import CsvColumnMapper from "@/components/CsvColumnMapper";
import JourneyHeader from "@/components/JourneyHeader";

const UploadScoreEngineResult = () => {
	const { setLoading } = useLoader();
	const [uploadedFile, setUploadedFile] = useState(null);
	const [currentSeparator, setCurrentSeparator] = useState("|");
	const [modelColumns, setModelColumns] = useState([]);
	const [csvColumns, setCsvColumns] = useState([]);
	const [mappings, setMappings] = useState({});
	const [unmatched, setUnmatched] = useState([]);

	// Fetch model columns on page load
	useEffect(() => {
		const loadModelColumns = async () => {
			setLoading(true);
			try {
				const res = await axios.post(`${API_BASE_URL}/api/get_model_column_names/scoreengineresponse/`);
				setModelColumns(res.data?.columns || []);
			} finally {
				setLoading(false);
			}
		};

		loadModelColumns();
	}, []);

	// When CSV is uploaded → autofill matching fields
	const handleCsvUpload = (cols) => {
		setCsvColumns(cols);

		const newMappings = {};
		const unmatchedList = [];

		modelColumns.forEach((mcol) => {
			const match = cols.find(
				(c) => c.toLowerCase().trim() === mcol.toLowerCase().trim()
			);
			newMappings[mcol] = match || "";
		});

		cols.forEach((c) => {
			const matched = modelColumns.some(
				(m) => m.toLowerCase().trim() === c.toLowerCase().trim()
			);
			if (!matched) unmatchedList.push(c);
		});

		setMappings(newMappings);
		setUnmatched(unmatchedList);
	};

	// Refresh unmatched columns
	const handleRefresh = () => {
		const filled = Object.values(mappings)
			.filter((v) => v && v.trim())
			.map((v) => v.toLowerCase());

		const remaining = csvColumns.filter(
			(col) => !filled.includes(col.toLowerCase())
		);

		setUnmatched(remaining);
	};

	const handleSubmit = async () => {
		if (!uploadedFile) {
			console.error("No file selected");
			return;
		}

		setLoading(true);

		try {
			const formData = new FormData();
			formData.append("file", uploadedFile);
			formData.append("mapping", JSON.stringify(mappings));
			formData.append("separator", currentSeparator); // assuming separator is in scope

			const res = await axios.post(
				`${API_BASE_URL}/api/submit_score_engine_response_pushload/`,
				formData
			);

			const data = res.data;

			// full response log
			console.log("Submit response:", data);

			// FIX: normalize failed rows
			let failedRows = [];

			try {
				failedRows = JSON.parse(data.failed_rows);
			} catch {
				failedRows = data.failed_rows;
			}

			if (!Array.isArray(failedRows)) failedRows = [];

			// now safe
			if (failedRows.length > 0) {
				console.group("FAILED ROWS");
				console.table(failedRows);
				console.groupEnd();

				alert(
					`${data.rows_added} rows added.\n` +
					`${failedRows.length} rows failed.\n\n` +
					`Check console for details.`
				);
			} else {
				alert(`${data.rows_added} rows successfully added.`);
			}

			// window.location.reload();
			setUploadedFile(null);
			setCsvColumns([]);
			setMappings({});
			setUnmatched([]);

		} catch (err) {
			console.error("Submit failed:", err);
			alert("Submit failed. Check console for details.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-intellend-white">
			{/* Compact header */}
			<JourneyHeader />
			<main className="container mx-auto px-3 py-4 max-w-2xl">
				<div className="flex flex-col">
					<div className="flex flex-col gap-3 pt-2">
						<h1 className="text-xl font-bold mb-4 text-center">Upload Score Engine Results</h1>
						<CsvColumnMapper
							modelColumns={modelColumns}
							csvColumns={csvColumns}
							mappings={mappings}
							setMappings={setMappings}
							unmatched={unmatched}
							onCsvUpload={(cols, file) => {
								if (file && !file.name.toLowerCase().endsWith(".csv")) {
									alert("Only CSV files are allowed.");
									return;
								}
								handleCsvUpload(cols);
								setUploadedFile(file);
								setCurrentSeparator(currentSeparator);
							}}
							onRefresh={handleRefresh}
							onSeparatorChange={setCurrentSeparator}
						/>
					</div>
					<div className="flex flex-col gap-3 pt-2">
						<Button
							onClick={() => handleSubmit(uploadedFile)}
							className="w-full sm:w-auto sm:ml-auto bg-intellend-green hover:bg-intellend-green/90 h-9"
						>
							Submit
						</Button>
					</div>
				</div>
			</main>
		</div>
	);
};

export default UploadScoreEngineResult;
